import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { InvoicesKeys, voidInvoice } from "@/features/invoices/api/invoices";
import { track } from "@/utils/mixpanel";

interface VoidInvoiceConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  invoiceNumber?: string;
}

const VoidInvoiceConfirmationModal = ({
  open,
  onOpenChange,
  invoiceId,
  invoiceNumber,
}: VoidInvoiceConfirmationModalProps) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate } = useMutation({
    mutationFn: voidInvoice,
    onMutate: () => {
      setIsSubmitting(true);
      toast.loading("Voiding invoice...", { id: "void-invoice" });
    },
    onSuccess: () => {
      toast.dismiss("void-invoice");
      toast.success("Invoice voided successfully");

      // Track void invoice action
      track("void_invoice", {
        invoice_id: invoiceId,
        invoice_number: invoiceNumber,
      });

      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: [InvoicesKeys.Invoices] });

      // Close the modal
      onOpenChange(false);
    },
    onError: (error) => {
      toast.dismiss("void-invoice");
      console.error("Error voiding invoice:", error);
      toast.error("Failed to void invoice. Please try again.");
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const handleConfirm = () => {
    mutate(invoiceId);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Void Invoice</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to void{" "}
            {invoiceNumber ? `invoice #${invoiceNumber}` : "this invoice"}?
            <br />
            <br />
            <span className="font-medium text-destructive">
              This action cannot be undone.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="bg-destructive hover:bg-destructive/90"
          >
            Void Invoice
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default VoidInvoiceConfirmationModal;
